import {
  ContentId,
  CreateOrUpdateShowcaseUpdateMessage,
  CreateTrustCenterContent,
  IObject,
  ShowcaseUpdateMessageId,
  TrustCenterConfigMeta,
  UpdateNotificationStatus,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function getConfig() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/trust_center/config`, {});
}

export async function getAuth() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/trust_center/auth`, {});
}

export async function updateIsPublic(isPublic: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/auth/is_public`,
    {body: isPublic},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterAuth.filter()]);
  return res;
}

export async function updatePassword(password: string | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/auth/password`,
    {body: password},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterAuth.filter()]);
  return res;
}

export async function updateConfigDocumentRequestEmail(email: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/config/document_request_email`,
    {body: email},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterConfig.filter()]);
  return res;
}

export async function updateConfigTheme(theme: IObject) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/config/theme`,
    {body: theme},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterConfig.filter()]);
  return res;
}

export async function updateConfigMeta(meta: TrustCenterConfigMeta) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/config/meta`,
    {body: meta},
  );
  await invalidateQueries([
    q.vendorToolkit.trustCenterConfig.filter(),
    q.vendorToolkit.trustCenterNewShowcaseMessages.filter(),
  ]);
  return res;
}

export async function listContents() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/trust_center/contents`, {});
}

export async function getContent(contentId: ContentId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/trust_center/contents/{content_id}`,
    {path: {content_id: contentId}},
  );
}

export async function createContent(content: CreateTrustCenterContent) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/contents`,
    {body: content},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterContents.filter()]);
  return res;
}

export async function updateContentTitle(contentId: ContentId, title: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/contents/{content_id}/title`,
    {body: title, path: {content_id: contentId}},
  );
  await invalidateQueries([
    q.vendorToolkit.trustCenterContents.filter(),
    q.vendorToolkit.trustCenterContent.filter(contentId),
  ]);
  return res;
}

export async function updateContentContent(contentId: ContentId, content: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/contents/{content_id}/content`,
    {body: content, path: {content_id: contentId}},
  );
  await invalidateQueries([
    q.vendorToolkit.trustCenterContent.filter(contentId),
    q.vendorToolkit.trustCenterNewShowcaseMessages.filter(),
  ]);
  return res;
}

export async function deleteContent(contentId: ContentId) {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/trust_center/contents/{content_id}`,
    {path: {content_id: contentId}},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterContents.filter()]);
  return res;
}

export async function getMailingList() {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/trust_center/mailing_list`,
    {},
  );
}

export async function createShowcaseMessage() {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/notification_center/messages`,
    {body: {}},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterDraftShowcaseMessages.filter()]);
  return res;
}

export async function updateShowcaseMessage(
  messageId: ShowcaseUpdateMessageId,
  saveRequest: CreateOrUpdateShowcaseUpdateMessage,
) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/notification_center/messages/{message_id}`,
    {body: saveRequest, path: {message_id: messageId}},
  );
  await invalidateQueries([q.vendorToolkit.trustCenterShowcaseMessage.filter(messageId)]);
  return res;
}

export async function sendShowcaseMessage(messageId: ShowcaseUpdateMessageId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/notification_center/messages/{message_id}/send`,
    {path: {message_id: messageId}},
  );
  await invalidateQueries([
    q.vendorToolkit.trustCenterShowcaseMessage.filter(messageId),
    q.vendorToolkit.trustCenterDraftShowcaseMessages.filter(),
    q.vendorToolkit.trustCenterSentShowcaseMessages.filter(),
    q.vendorToolkit.trustCenterNewShowcaseMessages.filter(),
  ]);
  return res;
}

export async function readShowcaseMessage(messageId: ShowcaseUpdateMessageId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/trust_center/notification_center/messages/{message_id}/read`,
    {path: {message_id: messageId}},
  );
  await invalidateQueries([
    q.vendorToolkit.trustCenterShowcaseMessage.filter(messageId),
    q.vendorToolkit.trustCenterNewShowcaseMessages.filter(),
    q.vendorToolkit.trustCenterDraftShowcaseMessages.filter(),
    q.vendorToolkit.trustCenterSentShowcaseMessages.filter(),
  ]);
  return res;
}

export async function getShowcaseMessage(messageId: ShowcaseUpdateMessageId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/trust_center/notification_center/messages/{message_id}`,
    {path: {message_id: messageId}},
  );
}

export async function listShowcaseMessages(status: UpdateNotificationStatus) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/trust_center/notification_center/messages/list`,
    {query: {status}},
  );
}

export async function deleteShowcaseMessage(messageId: ShowcaseUpdateMessageId) {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/trust_center/notification_center/messages/{message_id}`,
    {path: {message_id: messageId}},
  );
  await invalidateQueries([
    q.vendorToolkit.trustCenterShowcaseMessage.filter(messageId),
    q.vendorToolkit.trustCenterNewShowcaseMessages.filter(),
    q.vendorToolkit.trustCenterDraftShowcaseMessages.filter(),
    q.vendorToolkit.trustCenterSentShowcaseMessages.filter(),
  ]);
  return res;
}
